/**
 * An occupancy bar whose fill is split into labelled lanes (BON-478). Sibling of
 * {@link FillBar}, which answers "how full" with one number; this answers "full
 * of what".
 *
 * The denominator is `max(total, Σ segments)`, not `total`: capacity is a soft
 * target (D6), so segments can exceed it and would otherwise overflow the track.
 *
 * One progressbar, not three — a progressbar has one value, so the aggregate is
 * the accessible value and the breakdown rides in the label.
 */
export interface FillSegment {
  /** Legend text. */
  label: string;
  value: number;
  tone: 'booked' | 'reserved' | 'in-checkout';
}

export interface SegmentedFillBarProps {
  segments: FillSegment[];
  /** What a human reads as the denominator — capacity, not the scaling divisor. */
  total: number;
  ariaLabel: string;
  /** BON-490 — set by a KPI row on hover: the matching lane stays fully lit and
   *  every other lane dims to ~.3 opacity. `null`/omitted → no dimming. The bar
   *  has exactly one consumer (BookingsCard) today, so this stays optional. */
  highlightTone?: FillSegment['tone'] | null;
}

/** One source of truth for the lane→CSS-class mapping — shared by the bar and
 *  the BON-490 KPI cells' color-key dots. */
export const TONE_CLASS: Record<FillSegment['tone'], string> = {
  booked: 'nb-seg-booked',
  reserved: 'nb-seg-reserved',
  'in-checkout': 'nb-seg-in-checkout',
};

/** The lanes worth drawing — zero-value lanes are dropped. */
function visibleSegments(segments: FillSegment[]): FillSegment[] {
  return segments.filter((s) => s.value > 0);
}

export function SegmentedFillBar({
  segments,
  total,
  ariaLabel,
  highlightTone = null,
}: SegmentedFillBarProps) {
  const filled = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  // Never zero (capacity 0) and never below the fill (over capacity).
  const divisor = Math.max(1, total, filled);
  const shown = visibleSegments(segments);

  const breakdown = shown.map((s) => `${s.value} ${s.label}`).join(', ');

  return (
    <div
      className="nb-bar nb-bar-segmented"
      style={{ width: '100%' }}
      role="progressbar"
      aria-valuemin={0}
      // The divisor, not `total`: over capacity, `valuenow` would exceed the max
      // and invalidate the node. The honest 31/30 stays in the headline.
      aria-valuemax={divisor}
      aria-valuenow={filled}
      aria-label={breakdown ? `${ariaLabel} · ${breakdown}` : ariaLabel}
    >
      {shown.map((s) => {
        const dimmed = highlightTone !== null && s.tone !== highlightTone;
        return (
          <i
            key={s.tone}
            aria-hidden="true"
            className={TONE_CLASS[s.tone]}
            style={{
              width: `${(s.value / divisor) * 100}%`,
              opacity: dimmed ? 0.3 : 1,
              transition: 'opacity .12s',
            }}
            data-testid={`segment-${s.tone}`}
          />
        );
      })}
    </div>
  );
}
